/*
 * HR > Payroll: one pay week (Monday to Sunday), or two weeks when the company pays every two weeks. A card per person that
 * shows their total closed and their jobs, tips, hours, money from paid weeks and money they owe when opened; jobs paid with
 * nobody on them (No crew yet); Waiting on payment and If everyone paid; Mark paid with a confirm, Undo in More, and the
 * spreadsheet. A paid week shows exactly what was paid; later work goes to the next week.
 * HR > Time: everyone's clocked time for a week, each shift opening to change its times and to its activity log.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import {
  addDays,
  CAME_IN_AFTER,
  nextPeriod,
  payCalendar,
  periodEnd,
  periodOf,
  periodToShow,
  weekOf,
  WEEKLY,
  type PayCalendar,
  type PayWeekAnswer,
  type PayWeekLine,
  type ShiftEvent,
  type ShiftRow,
} from "@steward/shared";
import { useState } from "react";
import { ApiError } from "../api/client";
import { usePeople } from "../api/hooks";
import { useDeleteShift, usePayRun, usePayWeek, useSaveShift, useShiftActivity, useShifts } from "../api/payroll";
import { useSession } from "../app/session";
import { Combobox, InputText } from "../components/fields";
import { Menu, Modal, useToast } from "../components/overlays";
import { dayDate, minutesWords, moneyExact, plural, shortDate, todayIn } from "../components/format";
import {
  Avatar,
  Banner,
  Button,
  Card,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  Skeleton,
  StatusLabel,
} from "../components/parts";
import { Icon, type IconName } from "../components/Icon";
import { SettingsButton } from "./settings/Feature";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function WeekSwitch({
  start,
  today,
  cal,
  home,
  homeLabel,
  onChange,
}: {
  start: string;
  today: string;
  cal: PayCalendar;
  /** Where the middle button goes. Today's period when not given. */
  home?: string;
  homeLabel?: string;
  onChange: (s: string) => void;
}) {
  const to = home ?? periodOf(today, cal);
  return (
    <div className="day-switch">
      <IconButton icon="chevronLeft" label="Pay period before" onClick={() => onChange(periodOf(addDays(start, -1), cal))} />
      <button type="button" className="btn secondary small" disabled={start === to} onClick={() => onChange(to)}>
        {homeLabel ?? (cal.weeks === 2 ? "This pay period" : "This week")}
      </button>
      <IconButton icon="chevronRight" label="Pay period after" onClick={() => onChange(nextPeriod(start, cal))} />
      <span className="muted num">
        {shortDate(start)} to {shortDate(periodEnd(start, cal))}
      </span>
    </div>
  );
}

/** "this week" or "these two weeks", for a pay period's words. */
const spanWords = (d: PayWeekAnswer) => (d.days === 14 ? "these two weeks" : "this week");

const hours = (mins: number) => (mins ? minutesWords(mins) : "No hours");

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

function stateLabel(w: PayWeekAnswer) {
  if (w.state === "paid") return <StatusLabel status="success">Paid</StatusLabel>;
  if (w.state === "current")
    return <StatusLabel status="informative">{w.days === 14 ? "This pay period so far" : "This week so far"}</StatusLabel>;
  if (w.state === "future") return <StatusLabel status="inactive">Not started</StatusLabel>;
  return <StatusLabel status="warning">Not paid yet</StatusLabel>;
}

export function Payroll() {
  const { company, can } = useSession();
  const today = todayIn(company.timezone);
  // No start until the server says which pay period to open on: the one paid this payday, until the week it is paid in is
  // over (owner, 2026-11-02). Weekly or every two weeks.
  const [picked, setStart] = useState<string | undefined>(undefined);
  const w = usePayWeek(company.id, picked);
  const run = usePayRun();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  // Each person's jobs fold away; the total shows on the closed card (owner, 2026-10-30).
  const [open, setOpen] = useState<Set<string>>(new Set());
  const editable = can("payroll.update");
  const d = w.data;
  const allOpen = d ? d.people.length > 0 && d.people.every((p) => open.has(p.personId)) : false;
  const cal = d ? payCalendar(d.settings.periodWeeks, d.settings.twoWeekFrom) : payCalendar(1, null);
  const payWeek = periodToShow(today, cal);
  const start = picked ?? d?.start ?? payWeek;

  const act = (action: "paid" | "undo") =>
    run.mutate(
      { start, action, early: action === "paid" && Boolean(d?.early), noCrewOk: action === "paid" && Boolean(d?.noCrew.length) },
      {
        onSuccess: () => {
          setConfirming(false);
          toast(action === "paid" ? (d?.days === 14 ? "Pay period marked paid" : "Week marked paid") : "Paid mark taken back");
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : "That didn't save. Try again.", { kind: "error" }),
      },
    );

  const owing = d?.people.filter((p) => p.totalCents < 0) ?? [];
  const paying = d?.people.filter((p) => p.totalCents > 0) ?? [];
  const toPay = paying.reduce((a, p) => a + p.totalCents, 0);

  return (
    <Page
      title="Payroll"
      titleMetaData={d ? <> {stateLabel(d)}</> : null}
      subtitle={d ? `Payday ${dayDate(d.payday)}` : " "}
      primaryAction={
        editable && d && !d.paid && d.state !== "future" ? (
          <Button icon="check" onClick={() => setConfirming(true)}>
            Mark paid
          </Button>
        ) : null
      }
      secondaryAction={
        <>
          <SettingsButton to="/hr/settings">HR settings</SettingsButton>
          {d && !__PREVIEW__ ? (
            <Button type="secondary" icon="file" onClick={() => window.open(`/v1/payroll/${start}/csv`, "_blank")}>
              Download CSV
            </Button>
          ) : null}
        </>
      }
      moreActions={
        editable && d?.paid ? (
          <Menu
            button={({ toggle, open }) => (
              <button type="button" className="iconbtn" aria-label="More for this week" aria-expanded={open} onClick={toggle}>
                <Icon name="more" />
              </button>
            )}
          >
            {(close) => (
              <button
                type="button"
                disabled={Boolean(d.undoBlockedBy)}
                onClick={() => {
                  close();
                  act("undo");
                }}
              >
                Undo paid
              </button>
            )}
          </Menu>
        ) : null
      }
    >
      <WeekSwitch start={start} today={today} cal={cal} home={payWeek} homeLabel="This payday" onChange={setStart} />
      {w.error ? (
        <LoadError error={w.error} retry={() => void w.refetch()} />
      ) : !d ? (
        <Skeleton rows={4} />
      ) : (
        <div className={`stack${w.isPlaceholderData ? " refreshing" : ""}`}>
          <div className="tiles">
            <Tile label="Total pay" value={moneyExact(d.totalCents)} hint={plural(d.people.length, "person", "people")} />
            <Tile
              label="Jobs paid"
              value={String(d.jobs.length)}
              hint={`Paid from ${shortDate(d.start)} to ${shortDate(d.end)}`}
            />
            <Tile label="Waiting on payment" value={moneyExact(d.waiting.baseCents)} hint={plural(d.waiting.count, "job")} />
            <Tile
              label="If everyone paid"
              value={moneyExact(d.waiting.people.reduce((a, p) => a + p.payCents, 0))}
              hint="More pay once those jobs are paid"
            />
          </div>
          {d.paid ? (
            <Banner type="success">
              {d.paid.paidBy ? `${d.paid.paidBy} marked it paid on` : "Marked paid on"} {shortDate(d.paid.paidAt.slice(0, 10))}.
              This pay stays as it was paid. Work done or changed after that goes into the week of {shortDate(d.paid.nextStart)}.
              {d.undoBlockedBy ? ` To undo it, undo the week of ${shortDate(d.undoBlockedBy)} first.` : ""}
            </Banner>
          ) : null}
          {d.missingRates.length ? (
            <Banner type="warning" action={<TeamLink />}>
              {d.missingRates.join(", ")} {d.missingRates.length === 1 ? "is" : "are"} paid by the hour with no rate set, so their
              hours pay nothing yet.
            </Banner>
          ) : null}
          {owing.length && !d.paid ? (
            <Banner type="warning">
              {owing.map((p) => p.name).join(", ")} kept more client money than their pay {spanWords(d)}. They are paid nothing,
              and what they owe comes off their next pay.
            </Banner>
          ) : null}
          {d.noCrew.length ? <NoCrew d={d} /> : null}
          {d.people.length === 0 ? (
            <Card>
              <EmptyState icon="team" title={d.days === 14 ? "No pay these two weeks" : "No pay this week"}>
                When a client pays for a job, the people on it are paid here. Hours show once someone clocks in.
              </EmptyState>
            </Card>
          ) : (
            <div className="stack pay-people">
              <div className="row between">
                <h2 className="section-title">
                  Pay by person <span className="muted">({String(d.people.length)})</span>
                </h2>
                <Button
                  type="tertiary"
                  size="small"
                  icon={allOpen ? "chevronUp" : "chevronDown"}
                  onClick={() => setOpen(allOpen ? new Set() : new Set(d.people.map((p) => p.personId)))}
                >
                  {allOpen ? "Close all" : "Open all"}
                </Button>
              </div>
              {d.people.map((p) => (
                <PersonCard
                  key={p.personId}
                  d={d}
                  p={p}
                  open={open.has(p.personId)}
                  onToggle={() =>
                    setOpen((was) => {
                      const next = new Set(was);
                      if (!next.delete(p.personId)) next.add(p.personId);
                      return next;
                    })
                  }
                />
              ))}
            </div>
          )}
          {d.waiting.count ? <Waiting d={d} /> : null}
          <p className="muted">
            {d.days === 14 ? "Pay runs every two weeks. " : ""}
            Payday is {WEEKDAYS[d.settings.paydayWeekday - 1]} after the {d.days === 14 ? "two weeks end" : "week ends"}.{" "}
            {d.settings.defaultPoolPercent === null
              ? "There is no default pool, so each job pays the highest percent of the people on it."
              : `Each job pays at least a ${String(d.settings.defaultPoolPercent)}% pool, or the highest percent on it.`}{" "}
            {d.settings.commercialPoolPercent
              ? `Jobs for a business pay a ${String(d.settings.commercialPoolPercent)}% pool, whatever the crew's own percent.`
              : ""}{" "}
            {can("settings.read") ? (
              <Link to="/hr/settings" hash="payroll">
                Change it
              </Link>
            ) : (
              "Ask the owner to change it."
            )}
          </p>
        </div>
      )}
      <Modal
        title={d?.early ? "Pay this early?" : d?.days === 14 ? "Mark these two weeks paid" : "Mark this week paid"}
        open={confirming}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button icon="check" loading={run.isPending} onClick={() => act("paid")}>
              {d?.early ? "Yes, pay it early" : d?.noCrew.length ? "Yes, pay anyway" : "Mark paid"}
            </Button>
          </>
        }
      >
        {d?.noCrew.length ? (
          <Banner type="warning">
            {d.noCrew.length === 1 ? "1 job has" : `${String(d.noCrew.length)} jobs have`} no crew yet. Nobody gets paid for
            {d.noCrew.length === 1 ? " it" : " them"} this week unless you set who worked first.
          </Banner>
        ) : null}
        {d?.early ? (
          <Banner type="warning">
            This pay period is not over yet. It runs until {dayDate(d.end)}, and payday is {dayDate(d.payday)}. Jobs paid after
            today go into the next pay period.
          </Banner>
        ) : null}
        <p>
          Mark {shortDate(start)} to {shortDate(d?.end ?? addDays(start, 6))} paid? That is {moneyExact(toPay)} to{" "}
          {plural(paying.length, "person", "people")}.
        </p>
        {owing.length ? (
          <p>
            {owing.map((p) => `${p.name} owes ${moneyExact(-p.totalCents)}`).join(". ")}. They are paid nothing, and it comes off
            their next pay.
          </p>
        ) : null}
        <p className="muted">
          The pay stays as it is now. A job paid later, a late tip or a fixed clock goes into the next pay period that is not
          paid.
        </p>
      </Modal>
    </Page>
  );
}

function TeamLink() {
  const navigate = useNavigate();
  return (
    <Button type="secondary" size="small" onClick={() => void navigate({ to: "/hr/team" })}>
      Set rates
    </Button>
  );
}

const LATE_WORDS = {
  late: (from: string) => `From the week of ${shortDate(from)}, done after it was paid`,
  change: (from: string) => `Change after the week of ${shortDate(from)} was paid`,
  hours: (from: string) => `Change to hours for the week of ${shortDate(from)}`,
} as const;

/** A person's pay: closed shows the total and a one line summary, open shows each job. */
function PersonCard({ d, p, open, onToggle }: { d: PayWeekAnswer; p: PayWeekLine; open: boolean; onToggle: () => void }) {
  const { company } = useSession();
  const person = usePeople(company.id).data?.find((x) => x.id === p.personId);
  const jobs = d.jobs.flatMap((j) => {
    const s = j.shares.find((x) => x.personId === p.personId);
    return s ? [{ j, s }] : [];
  });
  const late = d.late.filter((r) => r.personId === p.personId);
  const owes = p.totalCents < 0;
  const summary = [
    p.jobs || p.payType !== "hourly" ? plural(p.jobs, "job") : null,
    hours(p.minutes),
    p.payType === "hourly" ? (p.hourlyPayCents === null ? "No rate set" : `${moneyExact(p.hourlyPayCents)} for hours`) : null,
    p.tipCents ? `${moneyExact(p.tipCents)} in tips` : null,
    late.length ? plural(late.length, "change from a paid week", "changes from paid weeks") : null,
    p.carryCents ? `${moneyExact(p.carryCents)} owed from before` : null,
  ].filter(Boolean);
  const bodyId = `pay-${p.personId}`;
  return (
    <section className={`card pay-person${open ? " open" : ""}`}>
      <button type="button" className="pay-person-head" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
        <Avatar name={p.name} person={person ?? { id: p.personId }} />
        <span className="who">
          <span className="name">
            {p.name} <span className="pay-kind">{p.payType === "hourly" ? "by the hour" : "by the job"}</span>
          </span>
          <span className="muted">{summary.join(" · ")}</span>
        </span>
        <span className="total num">
          {owes ? <StatusLabel status="critical">Owes {moneyExact(-p.totalCents)}</StatusLabel> : moneyExact(p.totalCents)}
        </span>
        <Icon name="chevronDown" size={20} />
      </button>
      {open ? (
        <div className="pay-person-body" id={bodyId}>
          <PersonJobs jobs={jobs} late={late} p={p} start={d.start} />
        </div>
      ) : null}
    </section>
  );
}

function PersonJobs({
  jobs,
  late,
  p,
  start,
}: {
  jobs: { j: PayWeekAnswer["jobs"][number]; s: PayWeekAnswer["jobs"][number]["shares"][number] }[];
  late: PayWeekAnswer["late"];
  p: PayWeekLine;
  start: string;
}) {
  // By the day the job was done. A job done in an earlier week counts here because it was paid here, so it says when.
  const byDate = [...jobs].sort((a, b) => a.j.date.localeCompare(b.j.date) || a.j.paidOn.localeCompare(b.j.paidOn));
  return (
    <table className="data">
      <tbody>
        {byDate.map(({ j, s }) => (
          <tr key={j.visitId}>
            <td>
              {shortDate(j.date)}
              {j.date < start ? <div className="muted">Paid {shortDate(j.paidOn)}</div> : null}
            </td>
            <td>
              <WorkLink id={j.visitId} byHand={j.byHand} name={j.clientName} />
              {j.commercial ? <span className="muted"> · Business</span> : null}
              {j.raised ? <span className="muted"> · Pool raised</span> : null}
              {j.kept ? (
                <div className="muted">
                  Kept the {moneyExact(s.heldCents)} cash
                  {s.payCents < 0 ? `, so the business cut of ${moneyExact(-s.payCents)} comes off other jobs` : ""}.
                </div>
              ) : null}
            </td>
            <td className="right num muted wide-only">
              {j.baseCents ? `${moneyExact(j.baseCents)} at ${String(j.poolPercent)}%` : "Tip paid later"}
            </td>
            <td className="right num">
              <Pay cents={s.payCents} tip={s.tipCents} />
            </td>
          </tr>
        ))}
        {late.map((r, i) => (
          <tr key={`late${String(i)}`}>
            <td>{r.date ? shortDate(r.date) : ""}</td>
            {r.clientName === CAME_IN_AFTER ? (
              <td>
                Fix to the week of {shortDate(r.from)}
                <div className="muted">
                  {r.payCents < 0
                    ? `The old Steward paid that week ${moneyExact(-r.payCents)} more than its jobs come to now, so it comes off here.`
                    : `That week's jobs come to ${moneyExact(r.payCents)} more now than the old Steward paid, so it is added here.`}
                </div>
              </td>
            ) : (
              <td>
                {r.clientName ?? "Hours"}
                <span className="muted"> · {LATE_WORDS[r.kind](r.from)}</span>
                {r.cash ? <span className="muted"> · Crew kept the cash</span> : null}
              </td>
            )}
            <td className="wide-only" />
            <td className="right num">
              <Pay cents={r.payCents + r.hoursCents} tip={r.tipCents} />
            </td>
          </tr>
        ))}
        {p.carryCents ? (
          <tr>
            <td />
            <td>
              Owed from earlier weeks
              {p.carryFrom ? <span className="muted"> · from the week of {shortDate(p.carryFrom)}</span> : null}
            </td>
            <td className="wide-only" />
            <td className="right num">{moneyExact(p.carryCents)}</td>
          </tr>
        ) : null}
        {p.payType === "hourly" ? (
          <tr>
            <td />
            <td>
              Hours<span className="muted"> · {hours(p.minutes)}</span>
            </td>
            <td className="wide-only" />
            <td className="right num">{p.hourlyPayCents === null ? "No rate set" : moneyExact(p.hourlyPayCents)}</td>
          </tr>
        ) : null}
        <tr className="total-row">
          <td />
          <td>Total</td>
          <td className="wide-only" />
          <td className="right num">
            <b>{moneyExact(p.totalCents)}</b>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** A job's link: its visit, or the invoice for one typed by hand. */
function WorkLink({ id, byHand, name }: { id: string; byHand?: boolean | undefined; name: string }) {
  return byHand ? (
    <>
      <Link to="/invoices/$id" params={{ id }}>
        {name}
      </Link>
      <span className="muted"> · Invoice typed by hand</span>
    </>
  ) : (
    <Link to="/visits/$ref" params={{ ref: id }}>
      {name}
    </Link>
  );
}

/** A pay amount with its tip under it. */
function Pay({ cents, tip }: { cents: number; tip: number }) {
  return (
    <>
      {moneyExact(cents)}
      {tip ? <div className="muted">+ {moneyExact(tip)} tip</div> : null}
    </>
  );
}

function NoCrew({ d }: { d: PayWeekAnswer }) {
  return (
    <Card title={`No crew yet (${String(d.noCrew.length)})`} flush>
      <p className="card-empty muted">
        These jobs are paid but nobody is set on them, so they pay nobody. Open each one and set who worked it.
      </p>
      <table className="data">
        <tbody>
          {d.noCrew.map((j) => (
            <tr key={j.visitId}>
              <td>{shortDate(j.date)}</td>
              <td>
                <WorkLink id={j.visitId} byHand={j.byHand} name={j.clientName} />
                {j.fromStart ? <span className="muted"> · from the week of {shortDate(j.fromStart)}</span> : null}
              </td>
              <td className="right num">{moneyExact(j.baseCents)}</td>
              <td className="right num muted">{j.tipCents ? `+ ${moneyExact(j.tipCents)} tip` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Waiting({ d }: { d: PayWeekAnswer }) {
  return (
    <Card title="If everyone paid" flush>
      <p className="card-empty muted">
        {plural(d.waiting.count, "job")} worth {moneyExact(d.waiting.baseCents)} are done and not paid yet, billed or not. Once
        paid, they add this to each person&apos;s pay in the week the money comes in. Only you see this.
      </p>
      <table className="data">
        <tbody>
          {d.waiting.people.map((p) => (
            <tr key={p.personId}>
              <td>{p.name}</td>
              <td className="muted">{plural(p.jobs, "job")}</td>
              <td className="right num">{p.hourly ? "Paid by the hour" : moneyExact(p.payCents)}</td>
            </tr>
          ))}
          {d.waiting.noCrewCents ? (
            <tr>
              <td>No crew yet</td>
              <td />
              <td className="right num muted">{moneyExact(d.waiting.noCrewCents)} of jobs</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </Card>
  );
}

// HR > Time ------------------------------------------------------------------------------------------------------

/** Where the phone was at a clock in or out, as a link to the map (question 51). */
function Spot({ where, label }: { where: ShiftRow["inWhere"]; label: string }) {
  if (!where) return null;
  const at = `${where.lat.toFixed(5)},${where.lng.toFixed(5)}`;
  return (
    <a
      href={`https://www.openstreetmap.org/?mlat=${where.lat}&mlon=${where.lng}#map=17/${at.replace(",", "/")}`}
      target="_blank"
      rel="noreferrer"
      title={where.accuracyM ? `Within about ${String(where.accuracyM)} meters` : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </a>
  );
}

/** On the clock for the longest a shift can be: they likely forgot to clock out. */
const stuck = (s: ShiftRow) => !s.outTime && s.minutes >= 16 * 60;

/*
 * HR > Time (owner, 2026-10-30): each shift is a closed row with its hours. Open it to change the times right there and
 * to see what the person did while on the clock.
 */
export function TimeSheet() {
  const { company, can } = useSession();
  const today = todayIn(company.timezone);
  const [start, setStart] = useState(weekOf(today));
  const list = useShifts(company.id, start, addDays(start, 6));
  const people = usePeople(company.id).data;
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const editable = can("payroll.update");
  const all = list.data ?? [];
  const rows = q.trim() ? all.filter((s) => s.personName.toLowerCase().includes(q.trim().toLowerCase())) : all;
  const total = rows.reduce((a, s) => a + s.minutes, 0);

  return (
    <Page
      title="Time"
      subtitle="Clocked time for each day. Open a shift to change its times or see what they did."
      secondaryAction={<SettingsButton to="/hr/settings">HR settings</SettingsButton>}
      primaryAction={
        editable ? (
          <Button icon="plus" onClick={() => setAdding(true)}>
            Add time
          </Button>
        ) : null
      }
    >
      <WeekSwitch start={start} today={today} cal={WEEKLY} onChange={setStart} />
      <section className="card">
        <div className="table-tools">
          <h2>
            {plural(rows.length, "shift")} · {minutesWords(total)}
          </h2>
          <div className="search" style={{ width: 240, flex: "none", maxWidth: "100%" }}>
            <Icon name="search" size={18} />
            <input aria-label="Search shifts" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        {list.error ? (
          <div className="table-empty" role="alert">
            <LoadError error={list.error} retry={() => void list.refetch()} />
          </div>
        ) : list.isLoading ? (
          <Skeleton rows={3} />
        ) : rows.length === 0 ? (
          <div className="table-empty">{q ? "Nobody by that name clocked in." : "Nobody clocked in this week."}</div>
        ) : (
          <div className={`shift-list${list.isPlaceholderData ? " refreshing" : ""}`}>
            {rows.map((s) => (
              <ShiftFold
                key={s.id}
                s={s}
                person={people?.find((p) => p.id === s.personId)}
                open={open === s.id}
                editable={editable}
                onToggle={() => setOpen(open === s.id ? null : s.id)}
              />
            ))}
          </div>
        )}
      </section>
      {adding ? <ShiftModal shift={null} day={start} onClose={() => setAdding(false)} /> : null}
    </Page>
  );
}

function ShiftFold({
  s,
  person,
  open,
  editable,
  onToggle,
}: {
  s: ShiftRow;
  person: { id: string; color?: string | null; photo?: string | null } | undefined;
  open: boolean;
  editable: boolean;
  onToggle: () => void;
}) {
  const bodyId = `shift-${s.id}`;
  return (
    <div className={`shift${open ? " open" : ""}`}>
      <button type="button" className="shift-head" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
        <span className="day">{dayDate(s.date)}</span>
        <span className="who">
          <Avatar name={s.personName} size="small" person={person ?? { id: s.personId }} />
          {s.personName}
        </span>
        <span className="when num">
          {s.outTime ? (
            `${s.inTime} to ${s.outTime}`
          ) : (
            <>
              {s.inTime}{" "}
              {stuck(s) ? (
                <StatusLabel status="warning">Forgot to clock out?</StatusLabel>
              ) : (
                <StatusLabel status="informative">On the clock</StatusLabel>
              )}
            </>
          )}
        </span>
        <span className="hours num">{minutesWords(s.minutes)}</span>
        <span className="where">
          {s.inWhere || s.outWhere ? (
            <>
              <Spot where={s.inWhere} label="In" />
              {s.inWhere && s.outWhere ? " · " : null}
              <Spot where={s.outWhere} label="Out" />
            </>
          ) : (
            <span className="muted">{s.source === "office" ? "Typed by the office" : "Not shared"}</span>
          )}
          {s.edited ? <span className="muted"> · Changed</span> : null}
        </span>
        <Icon name="chevronDown" size={20} />
      </button>
      {open ? (
        <div className="shift-body" id={bodyId}>
          <ShiftTimes s={s} editable={editable} />
          <ShiftLog s={s} />
        </div>
      ) : null}
    </div>
  );
}

/** The shift's times, changed in place. */
function ShiftTimes({ s, editable }: { s: ShiftRow; editable: boolean }) {
  const save = useSaveShift();
  const remove = useDeleteShift();
  const toast = useToast();
  const [date, setDate] = useState(s.date);
  const [inTime, setIn] = useState(s.inTime);
  const [outTime, setOut] = useState(s.outTime ?? "");
  const [note, setNote] = useState(s.note ?? "");
  const [arming, setArming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = date !== s.date || inTime !== s.inTime || outTime !== (s.outTime ?? "") || note !== (s.note ?? "");
  const reset = () => {
    setDate(s.date);
    setIn(s.inTime);
    setOut(s.outTime ?? "");
    setNote(s.note ?? "");
    setError(null);
  };
  const go = () =>
    save.mutate(
      {
        id: s.id,
        body: {
          personId: s.personId,
          date,
          inTime,
          outTime: outTime.trim() === "" ? null : outTime,
          note: note.trim() === "" ? null : note.trim(),
        },
      },
      {
        onSuccess: () => {
          setError(null);
          toast("Time changed");
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "That didn't save. Try again."),
      },
    );
  return (
    <div className="shift-times stack">
      <h3>Times</h3>
      <InputText label="Day" type="date" value={date} disabled={!editable} onChange={(e) => setDate(e.target.value)} />
      <div className="row">
        <InputText label="Clocked in" type="time" value={inTime} disabled={!editable} onChange={(e) => setIn(e.target.value)} />
        <InputText
          label="Clocked out"
          type="time"
          value={outTime}
          disabled={!editable}
          onChange={(e) => setOut(e.target.value)}
        />
      </div>
      {editable ? (
        <InputText
          label="Note"
          value={note}
          placeholder="Why it changed"
          onChange={(e) => setNote(e.target.value)}
          description="Leave Clocked out blank while they are on the clock. A shift is at most 16 hours."
        />
      ) : s.note ? (
        <p className="muted">{s.note}</p>
      ) : null}
      {error ? <Banner type="error">{error}</Banner> : null}
      {editable ? (
        <div className="row between">
          <Button
            type="tertiary"
            size="small"
            variation={arming ? "destructive" : "subtle"}
            loading={remove.isPending}
            onBlur={() => setArming(false)}
            onClick={() => {
              if (!arming) {
                setArming(true);
                return;
              }
              remove.mutate(s.id, {
                onSuccess: () => toast("Time removed"),
                onError: (e) => toast(e instanceof ApiError ? e.message : "That didn't save.", { kind: "error" }),
              });
            }}
          >
            {arming ? "Remove this time?" : "Remove"}
          </Button>
          <div className="row">
            {changed ? (
              <Button type="tertiary" size="small" onClick={reset}>
                Cancel
              </Button>
            ) : null}
            <Button size="small" disabled={!changed} loading={save.isPending} onClick={go}>
              Save times
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const LOG_ICON: Record<ShiftEvent["kind"], IconName> = {
  in: "clock",
  out: "clock",
  now: "clock",
  onmyway: "message",
  start: "play",
  pause: "stop",
  done: "check",
  photos: "camera",
  checklist: "checklist",
  office: "edit",
};

/** What they did from clocking in to clocking out, from the clock, their visits, texts and photos. */
function ShiftLog({ s }: { s: ShiftRow }) {
  const log = useShiftActivity(s.id);
  return (
    <div className="shift-log">
      <h3>Activity</h3>
      {log.error ? (
        <LoadError error={log.error} retry={() => void log.refetch()} />
      ) : !log.data ? (
        <Skeleton rows={3} />
      ) : (
        <ol className="shift-timeline">
          {log.data.map((e, i) => (
            <li key={`${e.at}${String(i)}`} className={`k-${e.kind}${e.off ? " off" : ""}`}>
              <span className="t num">{e.time}</span>
              <span className="dot">
                <Icon name={LOG_ICON[e.kind]} size={14} />
              </span>
              <span className="what">
                {e.visitRef ? (
                  <Link to="/visits/$ref" params={{ ref: e.visitRef }}>
                    {e.text}
                  </Link>
                ) : (
                  e.text
                )}
                {e.where ? (
                  <>
                    {" "}
                    · <Spot where={e.where} label="Map" />
                  </>
                ) : null}
                {e.off ? <span className="muted"> · Off the clock</span> : null}
              </span>
            </li>
          ))}
          {!log.data.some((e) => !["in", "out", "now", "office"].includes(e.kind)) ? (
            <li className="none muted">No visits, texts or photos from them in this shift.</li>
          ) : null}
        </ol>
      )}
      <p className="muted small">Visit starts and pauses show for 30 days.</p>
    </div>
  );
}

function ShiftModal({ shift, day, onClose }: { shift: ShiftRow | null; day: string; onClose: () => void }) {
  const { company } = useSession();
  const people = usePeople(company.id);
  const save = useSaveShift();
  const toast = useToast();
  const [personId, setPerson] = useState(shift?.personId ?? "");
  const [date, setDate] = useState(shift?.date ?? day);
  const [inTime, setIn] = useState(shift?.inTime ?? "08:00");
  const [outTime, setOut] = useState(shift?.outTime ?? "");
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    if (!personId) {
      setError("Pick who this time is for.");
      return;
    }
    save.mutate(
      { id: shift?.id, body: { personId, date, inTime, outTime: outTime.trim() === "" ? null : outTime } },
      {
        onSuccess: () => {
          toast(shift ? "Time changed" : "Time added");
          onClose();
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "That didn't save. Try again."),
      },
    );
  };
  return (
    <Modal
      title={shift ? "Change time" : "Add time"}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={go}>
            {shift ? "Save time" : "Add time"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Combobox
          label="Person"
          value={personId}
          onChange={setPerson}
          options={(people.data ?? []).filter((p) => p.active).map((p) => ({ value: p.id, label: p.name }))}
        />
        <InputText label="Day" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="row">
          <InputText label="Start" type="time" value={inTime} onChange={(e) => setIn(e.target.value)} />
          <InputText
            label="End"
            type="time"
            value={outTime}
            onChange={(e) => setOut(e.target.value)}
            description="Leave it blank while they are on the clock."
          />
        </div>
        {error ? <Banner type="error">{error}</Banner> : null}
      </div>
    </Modal>
  );
}
